import { ArrowLongRightIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import CircularProgress from "@mui/material/CircularProgress";
import classNames from "classnames";
import { EInvestorInvitationStatus } from "common/enums/InvestorInvitation/EInvestorInvitationStatus";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import { EUserFileType } from "common/enums/User/EUserFileType";
import InvestorInvitationLinkResponseResource from "common/resources/InvestorInvitation/InvestorInvitationLinkResponseResource";
import InvestorInvitationResponseResource from "common/resources/InvestorInvitation/InvestorInvitationResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Modal from "components/elements/Modal";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import Typography, { ITypo, ITypoColor, ITypoComponent } from "components/elements/Typography";
import { useHasRules } from "components/materials/HasRules";
import I18n from "components/materials/I18n";
import CreateSignatureModal from "components/pages/Account/CreateSigntureModal";
import { getDraftSubscriptionLink } from "components/pages/Subscriptions/tabs/InProgress";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import { useInvestorPortalAccess } from "contexts/InvestorPortalAccess";
import { UserContext } from "contexts/User";
import useOpenable from "hooks/useOpenable";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import InvestorInvitationService from "services/InvestorInvitationService";
import UserService from "services/UserService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";
import InvitationLinkModal from "./InvitationLinkModal";
import InviteClientModal from "./InviteClientModal";

const investorInvitationService = container.resolve(InvestorInvitationService);
const userService = container.resolve(UserService);
const modules = container.resolve(ModuleConfig).get().modules;

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" });
const dateTimeFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });

/** Un lien périmé n'est pas un statut enregistré : il se déduit de la date de validité tant que le dossier n'est pas envoyé. */
type DisplayStatus = EInvestorInvitationStatus | "expired";
type PendingAction = { type: "resend" | "revoke"; invitation: InvestorInvitationResponseResource };
type LinkResult = { invitation: InvestorInvitationResponseResource; link: string; emailDelivery: boolean; origin: "created" | "resent" | "copied" };
type RowHandlers = {
	isMiddleOffice: boolean;
	/** Lien déjà connu de cette visite (création, renvoi ou copie précédente). */
	link: string | undefined;
	onCopy: (invitation: InvestorInvitationResponseResource) => void;
	onResend: (invitation: InvestorInvitationResponseResource) => void;
	onRevoke: (invitation: InvestorInvitationResponseResource) => void;
};

/** Rythme de la relecture de la liste, tant que la page est affichée. */
const REFRESH_INTERVAL_MS = 30_000;

/** Tant que le dossier n'est pas envoyé, le lien peut être renvoyé (même périmé). */
const PENDING_STATUSES: EInvestorInvitationStatus[] = [EInvestorInvitationStatus.sent, EInvestorInvitationStatus.opened, EInvestorInvitationStatus.in_progress];

export default function InvestorInvitations() {
	const copy = I18n.asset.pages.investor_invitations;
	const { user, updateUser } = useContext(UserContext);
	const [invitations, setInvitations] = useState<InvestorInvitationResponseResource[]>([]);
	const { products, canInvite } = useInvestorPortalAccess();
	const isMiddleOffice = useHasRules({ AND: { access_platform__middle_office: true } });
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	// Liens déjà vus pendant cette visite : les autres sont redemandés à Wenimmo, qui en conserve le jeton chiffré (`isLinkAvailable`).
	const [links, setLinks] = useState<Record<string, string>>({});
	const [filters, setFilters] = useState({ search: "", status: "" });
	const [linkResult, setLinkResult] = useState<LinkResult | null>(null);
	const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
	const [isActing, setIsActing] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);
	const inviteModal = useOpenable();
	const signatureModal = useOpenable();
	// Compte les modifications faites ici (création, renvoi, révocation) : une relecture lancée avant l'une d'elles est abandonnée.
	const localChanges = useRef(0);

	const loadInvitations = useCallback(async () => {
		setIsLoading(true);
		setLoadError(false);
		try {
			setInvitations(await investorInvitationService.getAll());
		} catch {
			setLoadError(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadInvitations();
	}, [loadInvitations]);

	// L'avancement d'un dossier change sans action du conseiller (signature du client, validation) : la liste est relue sans
	// écran de chargement, au retour sur l'onglet puis à intervalles réguliers tant qu'il est affiché.
	useEffect(() => {
		if (isLoading || loadError) return;
		const refresh = async () => {
			if (document.visibilityState !== "visible") return;
			const changesBefore = localChanges.current;
			try {
				const latest = await investorInvitationService.getAll();
				if (localChanges.current === changesBefore) setInvitations(latest);
			} catch {
				// La liste affichée reste valable : la prochaine relecture réessaiera.
			}
		};
		const onVisibilityChange = () => void refresh();
		document.addEventListener("visibilitychange", onVisibilityChange);
		const interval = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.clearInterval(interval);
		};
	}, [isLoading, loadError]);

	const replaceInvitation = useCallback((updated: InvestorInvitationResponseResource) => {
		localChanges.current += 1;
		setInvitations((current) => current.map((invitation) => (invitation.id === updated.id ? updated : invitation)));
	}, []);

	// Le cachet est exigé pour créer une invitation, comme pour lancer une souscription.
	const openInvite = useCallback(() => {
		const hasSeal = user?.userToFiles?.some((file) => file.type === EUserFileType.SEAL);
		if (hasSeal) inviteModal.open();
		else signatureModal.open();
	}, [inviteModal, signatureModal, user]);

	const onSignatureUpdated = useCallback(() => {
		if (user?.email) void userService.getByEmail(user.email).then((updatedUser) => updateUser(updatedUser));
		signatureModal.close();
		inviteModal.open();
	}, [inviteModal, signatureModal, updateUser, user?.email]);

	const onCreated = useCallback(
		(response: InvestorInvitationLinkResponseResource) => {
			localChanges.current += 1;
			setInvitations((current) => [response.invitation, ...current]);
			setLinks((current) => ({ ...current, [response.invitation.id]: response.link }));
			inviteModal.close();
			setLinkResult({ ...response, origin: "created" });
		},
		[inviteModal],
	);

	const copyLink = useCallback(
		async (link: string) => {
			try {
				await navigator.clipboard.writeText(link);
				toast.success(copy.actions.link_copied);
			} catch {
				toast.error(copy.actions.copy_failed);
			}
		},
		[copy.actions.copy_failed, copy.actions.link_copied],
	);

	/** Recopie le lien d'une invitation : celui de la visite s'il est connu, sinon celui que Wenimmo conserve. */
	const copyInvitationLink = useCallback(
		async (invitation: InvestorInvitationResponseResource) => {
			let link = links[invitation.id];
			if (!link) {
				try {
					link = await investorInvitationService.getLink(invitation.id);
				} catch (error) {
					toast.error(getErrorMessage(error) ?? copy.actions.link_unavailable);
					return;
				}
				const fetched = link;
				setLinks((current) => ({ ...current, [invitation.id]: fetched }));
			}
			try {
				await navigator.clipboard.writeText(link);
				toast.success(copy.actions.link_copied);
			} catch {
				// Le navigateur refuse une copie qui suit un appel réseau : la fenêtre montre le lien, à copier d'un clic.
				setLinkResult({ invitation, link, emailDelivery: false, origin: "copied" });
			}
		},
		[copy.actions.link_copied, copy.actions.link_unavailable, links],
	);

	const askAction = useCallback((type: PendingAction["type"], invitation: InvestorInvitationResponseResource) => {
		setActionError(null);
		setPendingAction({ type, invitation });
	}, []);

	const closeAction = useCallback(() => {
		if (!isActing) setPendingAction(null);
	}, [isActing]);

	const confirmAction = useCallback(async () => {
		if (!pendingAction) return;
		setIsActing(true);
		setActionError(null);
		try {
			if (pendingAction.type === "resend") {
				const response = await investorInvitationService.resend(pendingAction.invitation.id);
				replaceInvitation(response.invitation);
				setLinks((current) => ({ ...current, [response.invitation.id]: response.link }));
				setPendingAction(null);
				setLinkResult({ ...response, origin: "resent" });
			} else {
				const invitation = await investorInvitationService.revoke(pendingAction.invitation.id);
				replaceInvitation(invitation);
				setLinks((current) => Object.fromEntries(Object.entries(current).filter(([id]) => id !== invitation.id)));
				setPendingAction(null);
				toast.success(copy.actions.revoked);
			}
		} catch (error) {
			setActionError(getErrorMessage(error) ?? copy.action_error);
		} finally {
			setIsActing(false);
		}
	}, [copy.action_error, copy.actions.revoked, pendingAction, replaceInvitation]);

	const onSearch = useCallback((search: string, selectedFilter?: string) => {
		// La recherche ne transmet pas le filtre courant : il est conservé tant que la liste déroulante ne change pas.
		setFilters((current) => ({ search, status: selectedFilter ?? current.status }));
	}, []);

	const rows = useMemo(
		() =>
			invitations
				.filter((invitation) => matchesFilters(invitation, filters))
				.map((invitation) =>
					buildRow(invitation, {
						isMiddleOffice,
						link: links[invitation.id],
						onCopy: (target) => void copyInvitationLink(target),
						onResend: (target) => askAction("resend", target),
						onRevoke: (target) => askAction("revoke", target),
					}),
				),
		[askAction, copyInvitationLink, filters, invitations, links, isMiddleOffice],
	);

	const statusOptions = useMemo(
		() => [
			...Object.values(EInvestorInvitationStatus).map((status) => ({ value: status, label: I18n.asset.enums.EInvestorInvitationStatus[status] })),
			{ value: "expired", label: copy.table.expired },
		],
		[copy.table.expired],
	);

	const pendingName = pendingAction ? getClientName(pendingAction.invitation) : "";

	return (
		<PageTemplate
			tabTitle={copy.page_title}
			headerTitle={copy.page_title}
			rightElement={
				canInvite ? (
					<Button icon={<UserPlusIcon />} iconposition="left" onClick={openInvite}>
						{copy.invite}
					</Button>
				) : undefined
			}>
			<div className={classes["root"]}>
				<div className={classes["introduction"]}>
					<Typography typo={ITypo.H2}>{copy.title}</Typography>
					<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_700}>
						{copy.description}
					</Typography>
				</div>

				{isLoading && (
					<div className={classes["loading"]}>
						<CircularProgress size={28} />
						<Typography typo={ITypo.P_MEDIUM}>{I18n.asset.common.loading}</Typography>
					</div>
				)}

				{!isLoading && loadError && (
					<div className={classes["load-error"]}>
						<MessageBox type="warning" text={copy.load_error} />
						<Button onClick={() => void loadInvitations()}>{copy.retry}</Button>
					</div>
				)}

				{!isLoading && !loadError && (
					<Table
						columnsHead={columnsHead}
						rows={rows}
						count={rows.length}
						placeholderSearchbar={copy.table.search_placeholder}
						onNext={null}
						onSearch={onSearch}
						selectOptions={statusOptions}
						selectPlaceholder={copy.table.all_statuses}
					/>
				)}
			</div>

			<InviteClientModal isOpen={inviteModal.isOpen} onClose={inviteModal.close} products={products} onCreated={onCreated} />

			{linkResult && <InvitationLinkModal result={linkResult} origin={linkResult.origin} onCopy={copyLink} onClose={() => setLinkResult(null)} />}

			<Modal isOpen={pendingAction !== null} onClose={closeAction}>
				{pendingAction && (
					<div className={classes["confirmation"]}>
						<Typography typo={ITypo.H1} component={ITypoComponent.H2}>
							{pendingAction.type === "resend" ? copy.resend_modal.title : copy.revoke_modal.title}
						</Typography>
						<Typography typo={ITypo.P_MEDIUM}>
							{pendingAction.type === "resend"
								? I18n.trslt(copy.resend_modal.text, { name: pendingName })
								: I18n.trslt(pendingAction.invitation.submittedAt ? copy.revoke_modal.text_submitted : copy.revoke_modal.text, { name: pendingName })}
						</Typography>
						{actionError && (
							<Typography typo={ITypo.P_SMALL} color={ITypoColor.ERROR_800}>
								{actionError}
							</Typography>
						)}
						<div className={classes["confirmation-buttons"]}>
							<Button variant={EButtonVariant.OUTLINED} onClick={closeAction} disabled={isActing}>
								{I18n.asset.common.cancel}
							</Button>
							<Button onClick={() => void confirmAction()} loading={isActing} disabled={isActing}>
								{pendingAction.type === "resend" ? copy.resend_modal.confirm : copy.revoke_modal.confirm}
							</Button>
						</div>
					</div>
				)}
			</Modal>

			<CreateSignatureModal
				isOpen={signatureModal.isOpen}
				onClose={signatureModal.close}
				onSignatureUpdated={onSignatureUpdated}
				title={I18n.asset.pages.account.create_signature}
				description={I18n.asset.pages.account.signature_info}
			/>
		</PageTemplate>
	);
}

function getClientName(invitation: InvestorInvitationResponseResource) {
	return `${invitation.firstName} ${invitation.lastName}`;
}

function getDisplayStatus(invitation: InvestorInvitationResponseResource): DisplayStatus {
	return invitation.isExpired ? "expired" : invitation.status;
}

function normalize(value: string) {
	return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function matchesFilters(invitation: InvestorInvitationResponseResource, filters: { search: string; status: string }) {
	if (filters.status && getDisplayStatus(invitation) !== filters.status) return false;
	const search = normalize(filters.search.trim());
	if (!search) return true;
	return normalize([getClientName(invitation), invitation.email, invitation.companyName, invitation.productName, invitation.reference].filter(Boolean).join(" ")).includes(
		search,
	);
}

/** Dernier signe de vie de l'investisseur : ouverture du lien, saisie ou envoi du dossier. */
function getLastActivity(invitation: InvestorInvitationResponseResource): Date | null {
	const dates = [invitation.openedAt, invitation.lastActivityAt, invitation.submittedAt].filter((date): date is Date => date instanceof Date);
	if (!dates.length) return null;
	return new Date(Math.max(...dates.map((date) => date.getTime())));
}

/** Brouillon : le tunnel de souscription. Dossier déjà soumis : son récapitulatif. Brouillon archivé : aucun lien. */
function getDossierLink(invitation: InvestorInvitationResponseResource, isMiddleOffice: boolean): string | null {
	const subscription = invitation.subscription;
	if (!subscription || subscription.isArchived) return null;
	if (isMiddleOffice)
		return subscription.productSubscribedId ? modules.pages.MOSubscriptionSummary.props.path.replace(":productSubscribedId", subscription.productSubscribedId) : null;
	if (!subscription.step || subscription.step === ESubStep.draft) return getDraftSubscriptionLink(subscription.subscriptionType, subscription.id) || null;
	if (!subscription.productSubscribedId) return null;
	return modules.pages.SubscriptionInformation.props.path.replace(":productSubscribedId", subscription.productSubscribedId);
}

type DossierStep = keyof typeof I18n.asset.pages.investor_invitations.table.dossier_steps;

/**
 * Avancement du dossier une fois que le conseiller l'a soumis : traitement, signature, transmission, validation.
 * L'invitation, elle, s'arrête à « Dossier envoyé » : sans cela, un dossier signé ou validé garderait ce statut.
 * Rien tant que le dossier est un brouillon, ni s'il est archivé ou si l'invitation est révoquée.
 */
function getDossierStep(invitation: InvestorInvitationResponseResource): DossierStep | null {
	const subscription = invitation.subscription;
	if (invitation.status !== EInvestorInvitationStatus.submitted || !subscription?.step || subscription.isArchived) return null;
	switch (subscription.step) {
		case ESubStep.draft:
			return null;
		// Jamais posée par le serveur : la contrepartie précède la signature.
		case ESubStep.waiting_contrepartie:
			return ESubStep.waiting_signature;
		default:
			return subscription.step;
	}
}

function getValidity(invitation: InvestorInvitationResponseResource): string | null {
	const copy = I18n.asset.pages.investor_invitations.table;
	if (invitation.submittedAt || invitation.status === EInvestorInvitationStatus.revoked) return null;
	const date = dateFormat.format(invitation.expiresAt);
	return I18n.trslt(invitation.isExpired ? copy.expired_on : copy.valid_until, { date });
}

function buildRow(invitation: InvestorInvitationResponseResource, handlers: RowHandlers): IRowProps {
	const copy = I18n.asset.pages.investor_invitations;
	const displayStatus = getDisplayStatus(invitation);
	const isPending = PENDING_STATUSES.includes(invitation.status) && !invitation.submittedAt;
	const lastActivity = getLastActivity(invitation);
	const validity = getValidity(invitation);
	const dossierLink = getDossierLink(invitation, handlers.isMiddleOffice);
	const invitationStatusLabel = displayStatus === "expired" ? copy.table.expired : I18n.asset.enums.EInvestorInvitationStatus[displayStatus];
	const dossierStep = getDossierStep(invitation);
	const dossierDetail = dossierStep && dossierStep in copy.table.dossier_details ? copy.table.dossier_details[dossierStep as keyof typeof copy.table.dossier_details] : null;
	// Le lien se recopie tant qu'il fonctionne : avant l'envoi s'il n'est pas périmé, et après, où il sert au suivi.
	const canCopy = invitation.status !== EInvestorInvitationStatus.revoked && !invitation.isExpired && (Boolean(handlers.link) || invitation.isLinkAvailable);

	return {
		rowKey: invitation.id,
		client: (
			<div className={classes["cell"]}>
				<Typography typo={ITypo.P_SMALL_BOLD}>{getClientName(invitation)}</Typography>
				{invitation.companyName && <span className={classes["secondary"]}>{invitation.companyName}</span>}
				<span className={classes["secondary"]}>{invitation.email}</span>
			</div>
		),
		product: (
			<div className={classes["cell"]}>
				<Typography typo={ITypo.P_SMALL}>{invitation.productName}</Typography>
			</div>
		),
		status: (
			<div className={classes["cell"]}>
				<span className={classes["status"]} data-status={dossierStep ?? displayStatus}>
					{dossierStep ? copy.table.dossier_steps[dossierStep] : invitationStatusLabel}
				</span>
				{dossierDetail && <span className={classes["secondary"]}>{dossierDetail}</span>}
				{invitation.reference && (
					<span className={classNames(classes["secondary"], classes["nowrap"])}>{I18n.trslt(copy.table.reference, { reference: invitation.reference })}</span>
				)}
				{invitation.subscription?.isArchived && <span className={classes["secondary"]}>{copy.table.archived}</span>}
			</div>
		),
		sent_at: (
			<div className={classes["cell"]}>
				<span className={classes["nowrap"]}>{dateFormat.format(invitation.sentAt)}</span>
				{validity && <span className={classNames(classes["secondary"], classes["nowrap"])}>{validity}</span>}
			</div>
		),
		last_activity: lastActivity ? <span className={classes["nowrap"]}>{dateTimeFormat.format(lastActivity)}</span> : null,
		actions: (
			<div className={classes["actions"]}>
				{dossierLink && (
					<Link to={dossierLink}>
						<Button variant={EButtonVariant.TEXT} icon={<ArrowLongRightIcon />}>
							{copy.actions.open_dossier}
						</Button>
					</Link>
				)}
				{canCopy && (
					<Button variant={EButtonVariant.TEXT} onClick={() => handlers.onCopy(invitation)}>
						{copy.actions.copy_link}
					</Button>
				)}
				{isPending && (
					<Button variant={EButtonVariant.TEXT} onClick={() => handlers.onResend(invitation)}>
						{copy.actions.resend}
					</Button>
				)}
				{invitation.status !== EInvestorInvitationStatus.revoked && (
					<Button variant={EButtonVariant.TEXT} onClick={() => handlers.onRevoke(invitation)}>
						{copy.actions.revoke}
					</Button>
				)}
			</div>
		),
	};
}

/** Message d'une règle métier refusée par l'API (`{ property: "form", constraints: { error } }`), s'il y en a un. */
function getErrorMessage(error: unknown): string | null {
	if (!Array.isArray(error)) return null;
	for (const item of error as Partial<ValidationError>[]) {
		const message = item?.constraints?.["error"];
		if (typeof message === "string" && message !== "unknown error") return message;
	}
	return null;
}

const columnsHead: readonly TableColumn[] = [
	{ headContent: I18n.asset.pages.investor_invitations.table.client, key: "client" },
	{ headContent: I18n.asset.pages.investor_invitations.table.product, key: "product" },
	{ headContent: I18n.asset.pages.investor_invitations.table.status, key: "status" },
	{ headContent: I18n.asset.pages.investor_invitations.table.sent_at, key: "sent_at" },
	{ headContent: I18n.asset.pages.investor_invitations.table.last_activity, key: "last_activity" },
	{ headContent: I18n.asset.pages.investor_invitations.actions.label, key: "actions" },
];
